import { createTheme } from "@mui/material/styles";

const ORANGE       = "#ED9519";
const ORANGE_DARK  = "#c47510";
const ORANGE_CONTRAST = "#1a0e00";

// ─── Overrides compartidos (no dependen del modo) ─────────────────────────────
const sharedComponents = {
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: ORANGE,
        color: ORANGE_CONTRAST,
        boxShadow: "none",
        borderBottom: `1px solid ${ORANGE_DARK}`,
      },
    },
  },
  MuiToolbar: {
    styleOverrides: {
      root: { "& .MuiIconButton-root": { color: ORANGE_CONTRAST } },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: { textTransform: "none", fontWeight: 500, borderRadius: 8 },
      containedPrimary:  { backgroundColor: ORANGE, color: ORANGE_CONTRAST, "&:hover": { backgroundColor: ORANGE_DARK } },
      containedSuccess:  { backgroundColor: ORANGE, color: ORANGE_CONTRAST, "&:hover": { backgroundColor: ORANGE_DARK } },
      outlinedPrimary:   { borderColor: ORANGE, color: ORANGE, "&:hover": { backgroundColor: "rgba(237,149,25,0.08)" } },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: { borderRadius: 4, height: 4 },
      bar:  { backgroundColor: ORANGE, borderRadius: 4 },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        margin: "2px 8px",
        "&:hover": { backgroundColor: "rgba(237,149,25,0.1)" },
        "&.Mui-selected": {
          backgroundColor: "rgba(237,149,25,0.15)",
          "& .MuiListItemIcon-root": { color: ORANGE },
        },
      },
    },
  },
  MuiAlert: { styleOverrides: { root: { borderRadius: 8 } } },
  MuiChip: {
    styleOverrides: {
      root: { borderRadius: 20, fontWeight: 500 },
      colorPrimary: {
        backgroundColor: "rgba(237,149,25,0.15)",
        color: ORANGE,
        border: `1px solid rgba(237,149,25,0.35)`,
        "&:hover": { backgroundColor: "rgba(237,149,25,0.22)" },
      },
    },
  },
};

// ─── Tema oscuro ──────────────────────────────────────────────────────────────
export const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary:    { main: ORANGE, dark: ORANGE_DARK, contrastText: ORANGE_CONTRAST },
    secondary:  { main: ORANGE, contrastText: ORANGE_CONTRAST },
    success:    { main: ORANGE, dark: ORANGE_DARK, contrastText: ORANGE_CONTRAST },
    background: { default: "#111318", paper: "#1c1f27" },
    text:       { primary: "rgba(255,255,255,0.87)", secondary: "rgba(255,255,255,0.5)", disabled: "rgba(255,255,255,0.3)" },
    divider:    "rgba(255,255,255,0.08)",
  },
  shape: { borderRadius: 10 },
  typography: { fontFamily: '"Roboto","Helvetica","Arial",sans-serif', h6: { fontWeight: 500 } },
  components: {
    ...sharedComponents,
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "#111318",
          scrollbarColor: "#2a2d35 #111318",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "#111318" },
          "&::-webkit-scrollbar-thumb": { background: "#2a2d35", borderRadius: 3 },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundColor: "#16191f", borderRight: "1px solid rgba(255,255,255,0.06)" },
      },
    },
    MuiListItemIcon: { styleOverrides: { root: { color: "rgba(255,255,255,0.6)", minWidth: 40 } } },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none", backgroundColor: "#1c1f27", border: "1px solid rgba(255,255,255,0.07)" },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: "#12141a",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.1)" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.25)" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ORANGE },
        },
        input: {
          "&:-webkit-autofill": {
            WebkitBoxShadow: "0 0 0 100px #12141a inset",
            WebkitTextFillColor: "rgba(255,255,255,0.87)",
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { color: "rgba(255,255,255,0.45)", "&.Mui-focused": { color: ORANGE } } },
    },
    MuiCheckbox: {
      styleOverrides: { root: { color: "rgba(255,255,255,0.3)", "&.Mui-checked": { color: ORANGE } } },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { backgroundColor: "#1c1f27", backgroundImage: "none", border: "1px solid rgba(255,255,255,0.08)" },
      },
    },
    MuiSelect: { styleOverrides: { icon: { color: "rgba(255,255,255,0.4)" } } },
    MuiTableHead: {
      styleOverrides: { root: { "& .MuiTableCell-root": { backgroundColor: "#14171e", fontWeight: 500 } } },
    },
    MuiTableCell: {
      styleOverrides: { root: { borderBottom: "1px solid rgba(255,255,255,0.06)" } },
    },
    MuiMenu: {
      styleOverrides: { paper: { backgroundColor: "#1e2230", border: "1px solid rgba(255,255,255,0.1)" } },
    },
    MuiMenuItem: {
      styleOverrides: { root: { fontSize: 14, "&:hover": { backgroundColor: "rgba(237,149,25,0.08)" } } },
    },
  },
});

// ─── Tema claro ───────────────────────────────────────────────────────────────
export const lightTheme = createTheme({
  palette: {
    mode: "light",
    primary:    { main: ORANGE, dark: ORANGE_DARK, contrastText: ORANGE_CONTRAST },
    secondary:  { main: ORANGE, contrastText: ORANGE_CONTRAST },
    success:    { main: ORANGE, dark: ORANGE_DARK, contrastText: ORANGE_CONTRAST },
    background: { default: "#f4f6fb", paper: "#ffffff" },
    text:       { primary: "#111318", secondary: "#6b7280", disabled: "#b0b8c8" },
    divider:    "rgba(0,0,0,0.08)",
  },
  shape: { borderRadius: 10 },
  typography: { fontFamily: '"Roboto","Helvetica","Arial",sans-serif', h6: { fontWeight: 500 } },
  components: {
    ...sharedComponents,
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "#f4f6fb",
          scrollbarColor: "#c5cad6 #f4f6fb",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "#f4f6fb" },
          "&::-webkit-scrollbar-thumb": { background: "#c5cad6", borderRadius: 3 },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundColor: "#ffffff", borderRight: "1px solid rgba(0,0,0,0.08)" },
      },
    },
    MuiListItemIcon: { styleOverrides: { root: { color: "#6b7280", minWidth: 40 } } },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none", backgroundColor: "#ffffff", border: "1px solid rgba(0,0,0,0.07)" },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: "#f8f9fd",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dde1ec" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#b0b8c8" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ORANGE },
        },
        input: {
          "&:-webkit-autofill": {
            WebkitBoxShadow: "0 0 0 100px #f8f9fd inset",
            WebkitTextFillColor: "#111318",
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { color: "#9aa0b4", "&.Mui-focused": { color: ORANGE } } },
    },
    MuiCheckbox: {
      styleOverrides: { root: { color: "#b0b8c8", "&.Mui-checked": { color: ORANGE } } },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { backgroundColor: "#ffffff", backgroundImage: "none", border: "1px solid rgba(0,0,0,0.1)" },
      },
    },
    MuiSelect: { styleOverrides: { icon: { color: "#9aa0b4" } } },
    MuiTableHead: {
      styleOverrides: { root: { "& .MuiTableCell-root": { backgroundColor: "#eef0f7", fontWeight: 500, color: "#6b7280" } } },
    },
    MuiTableCell: {
      styleOverrides: { root: { borderBottom: "1px solid rgba(0,0,0,0.06)" } },
    },
    MuiMenu: {
      styleOverrides: { paper: { backgroundColor: "#ffffff", border: "1px solid rgba(0,0,0,0.1)", boxShadow: "0 4px 16px rgba(0,0,0,0.1)" } },
    },
    MuiMenuItem: {
      styleOverrides: { root: { fontSize: 14, "&:hover": { backgroundColor: "rgba(237,149,25,0.07)" } } },
    },
  },
});
